import { useNavigate } from "react-router-dom";
import { useToasts } from "../../contexts/toasts";
import { useCallback, useEffect, useMemo, useState } from "react";
import { Billing } from "../../types/Billing";
import { Form, Input } from "antd";
import { useAuth } from "../../contexts/auth";
import { TailSpin } from "react-loader-spinner";
import { Col, Container, Row } from "react-bootstrap";
import classNames from "classnames";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faCancel,
  faPencil,
  faSave,
} from "@fortawesome/free-solid-svg-icons";
import { Billings } from "../../api/Billings";

const formLayout = {
  labelCol: {
    xs: {
      span: 24,
    },
    md: {
      span: 24,
    },
  },
  wrapperCol: {
    xs: {
      span: 24,
    },
    md: {
      span: 16,
    },
  },
};

export default function BillingInfos() {
  const { showToast } = useToasts();
  const navigate = useNavigate();
  const { isBillingDataReady, billingData, setBillingData } =
    useAuth();
  const [billingInfos, setBillingInfos] = useState<Billing>();
  const [editLoading, setEditLoading] = useState(false);
  const [editing, setEditing] = useState(false);
  const [form] = Form.useForm();

  useEffect(() => {
    if (isBillingDataReady && !billingData) {
      navigate("/");
    } else {
      setBillingInfos(billingData);
    }
  }, [billingData, isBillingDataReady, navigate]);

  const onFieldChange = useCallback(
    (key: string) => (evt: React.ChangeEvent<HTMLInputElement>) => {
      const value = evt.target.value;
      setBillingInfos((previous) => ({
        ...previous!,
        [key]: value,
      }));
    },
    []
  );

  const handleEditBillingData = useCallback(() => {
    setEditLoading(true);
    Billings.updateBillingData(billingData!.id, billingInfos!).then(
      ({ data }) => {
        setEditLoading(false);
        showToast(
          "billing-data-update-success",
          "Dati di Fatturazione correttamente aggiornati",
          "",
          "success"
        );
        setBillingData(data.billing);
      },
      () => {
        showToast(
          "billing-data-update-error",
          "Errore nell'aggiornamento dei Dati di Fatturazione",
          "",
          "danger"
        );
        setEditLoading(false);
      }
    );
  }, [billingInfos, billingData, setBillingData, showToast]);

  const handleEditCloseClick = useCallback(() => {
    setBillingInfos(billingData);
    setEditing(false);
  }, [billingData]);

  const handleEditSubmit = useCallback(() => {
    if (editing) {
      handleEditBillingData();
    } else {
      setEditing(true);
    }
  }, [editing, handleEditBillingData]);

  const renderAsyncContent = () => {
    return !isBillingDataReady ? (
      <div className="d-flex justify-content-center py-5 my-5">
        <TailSpin height={100} width={100} color="#f25d0b" radius="1" />
      </div>
    ) : (
      <>
        {billingInfos && (
          <Row>
            <Col xs={12} md={6}>
              <Form.Item labelAlign="left" label="Nome della Società">
                <Input
                  className="w-100"
                  disabled={!editing}
                  placeholder="Nome della Società..."
                  value={billingInfos!.name}
                  onChange={onFieldChange("name")}
                />
              </Form.Item>
              <Form.Item labelAlign="left" label="Partita IVA">
                <Input
                  disabled={!editing}
                  placeholder="Partita IVA..."
                  value={billingInfos?.vatCode || ""}
                  onChange={onFieldChange("vatCode")}
                />
              </Form.Item>
              <Form.Item
                rules={[
                  {
                    type: "email",
                    message: "Inserisci una PEC valida",
                  },
                ]}
                labelAlign="left"
                label="PEC"
              >
                <Input
                  type="email"
                  disabled={!editing}
                  placeholder="PEC..."
                  value={billingInfos?.pec || ""}
                  onChange={onFieldChange("pec")}
                />
              </Form.Item>
              <Form.Item labelAlign="left" label="Codice Univoco">
                <Input
                  disabled={!editing}
                  placeholder="Codice Univoco..."
                  value={billingInfos?.uniCode || ""}
                  onChange={onFieldChange("uniCode")}
                />
              </Form.Item>
            </Col>
          </Row>
        )}
      </>
    );
  };

  const isDisabledSubmitButton = useMemo(() => {
    return (
      billingInfos?.name === billingData?.name &&
      billingInfos?.vatCode === billingData?.vatCode &&
      billingInfos?.pec === billingData?.pec &&
      billingInfos?.uniCode === billingData?.uniCode
    );
  }, [billingInfos, billingData]);

  const renderToolbar = () => {
    return (
      <Row className="mb-2 align-items-center">
        <Col>
          <h4 className="mb-0">I tuoi Dati di Fatturazione</h4>
        </Col>

        <Col xs={6} md={4} className="text-end">
          <button
            disabled={editLoading || (editing && isDisabledSubmitButton)}
            type="submit"
            className={classNames("button-primary", {
              "button-disabled":
                editLoading || (editing && isDisabledSubmitButton),
            })}
          >
            {editLoading ? (
              <TailSpin color="#FFF" width={15} height={15} />
            ) : (
              <>
                <FontAwesomeIcon icon={editing ? faSave : faPencil} />
                <span className="d-none d-sm-inline ms-2">
                  {editing ? "Salva Modifiche" : "Modifica Dati"}
                </span>
              </>
            )}
          </button>
          {editing && (
            <button
              disabled={editLoading}
              onClick={handleEditCloseClick}
              className={classNames("button-secondary", "ms-2", {
                "button-disabled": editLoading || editing,
              })}
            >
              {editLoading ? (
                <TailSpin color="#FFF" width={15} height={15} />
              ) : (
                <>
                  <FontAwesomeIcon icon={faCancel} />
                  <span className="d-none d-sm-inline ms-2">Annulla</span>
                </>
              )}
            </button>
          )}
        </Col>
      </Row>
    );
  };

  return (
    <Container fluid="md" className="mt-5">
      <Form {...formLayout} form={form} onFinish={handleEditSubmit}>
        {renderToolbar()}
        <hr />
        {renderAsyncContent()}
      </Form>
    </Container>
  );
}
